import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  User,
  ListUsersResponse,
  CreateUserRequest,
  SetPasswordRequest,
  ApiResponse,
} from '../types';
import { authService } from './auth.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const API_KEY = import.meta.env.VITE_API_KEY || '';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': API_KEY,
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('cognito_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          authService.forceLogout();
        }
        return Promise.reject(error);
      }
    );
  }

  async listUsers(
    limit = 20,
    paginationToken?: string
  ): Promise<{ users: User[]; paginationToken?: string }> {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (paginationToken) {
      params.append('paginationToken', paginationToken);
    }

    const response = await this.client.get<ListUsersResponse>(`/users?${params.toString()}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch users');
    }

    return response.data.data;
  }

  async createUser(request: CreateUserRequest): Promise<User> {
    const payload: CreateUserRequest = {
      ...request,
      username: request.username.toLowerCase(),
      email: request.email.toLowerCase(),
    };
    const response = await this.client.post<ApiResponse<User>>('/users', payload);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create user');
    }

    return response.data.data;
  }

  async deleteUser(username: string): Promise<void> {
    await this.client.delete(`/users/${encodeURIComponent(username.toLowerCase())}`);
  }

  async setUserPassword(username: string, request: SetPasswordRequest): Promise<void> {
    const response = await this.client.post<ApiResponse>(
      `/users/${encodeURIComponent(username.toLowerCase())}/password`,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to set password');
    }
  }

  async toggleAdmin(username: string): Promise<{ isAdmin: boolean }> {
    const response = await this.client.post<ApiResponse<{ isAdmin: boolean }>>(
      `/users/${encodeURIComponent(username.toLowerCase())}/admin`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to toggle admin status');
    }

    return response.data.data;
  }

  async toggleEnabled(username: string): Promise<{ enabled: boolean }> {
    const response = await this.client.post<ApiResponse<{ enabled: boolean }>>(
      `/users/${encodeURIComponent(username.toLowerCase())}/enabled`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to toggle enabled status');
    }

    return response.data.data;
  }

  setToken(token: string): void {
    localStorage.setItem('cognito_token', token);
  }

  clearToken(): void {
    localStorage.removeItem('cognito_token');
  }
}

export const apiService = new ApiService();
